<template>
    <v-card outlined class="mail-inbox fill-height">
        <v-card-title class="inbox-title">
            <div>
                <div class="subtitle-1 font-weight-bold">Conversazioni ricevute</div>
                <div class="caption grey--text text--darken-1">
                    {{ groupedMessages.length }} in pagina · {{ pagination.total || messages.length }} mail trovate
                </div>
            </div>
            <v-progress-circular v-if="loading" indeterminate size="22" width="2" color="primary" />
        </v-card-title>

        <v-card-text class="pb-2">
            <v-btn-toggle v-model="workFilter" mandatory dense class="queue-filter mb-3" aria-label="Filtro coda">
                <v-btn small value="work">Da lavorare</v-btn>
                <v-btn small value="completed">Completate</v-btn>
                <v-btn small value="all">Tutte</v-btn>
            </v-btn-toggle>
            <v-text-field
                v-model="search"
                dense
                outlined
                clearable
                hide-details
                label="Cerca in tutta la casella"
                prepend-inner-icon="mdi-magnify"
            />
            <v-row dense class="mt-1">
                <v-col cols="7">
                    <v-select v-model="statusFilter" :items="statusOptions" dense outlined hide-details label="Stato" />
                </v-col>
                <v-col cols="5">
                    <v-select v-model="assignedTo" :items="operatorOptions" dense outlined hide-details label="Responsabile" />
                </v-col>
            </v-row>
            <div class="d-flex align-center mt-1 px-1">
                <v-checkbox v-model="overdue" dense hide-details label="Solo oltre SLA" class="mt-0" />
                <v-spacer />
                <v-btn v-if="filtersActive" x-small text color="primary" @click="clearFilters">Azzera</v-btn>
            </div>
        </v-card-text>

        <v-list v-if="groupedMessages.length" two-line class="mail-list" role="list">
            <v-list-item
                v-for="message in groupedMessages"
                :key="message.conversation?.key || message.uid"
                :class="{ 'selected-message': message.uid === selectedUid }"
                role="listitem"
                tabindex="0"
                :aria-current="message.uid === selectedUid ? 'true' : undefined"
                @click="$emit('select', message)"
                @keydown.enter="$emit('select', message)"
                @keydown.space.prevent="$emit('select', message)"
            >
                <v-list-item-content>
                    <v-list-item-title class="d-flex align-center">
                        <span class="text-truncate font-weight-medium">{{ senderLabel(message) }}</span>
                        <v-spacer />
                        <time class="caption ml-2" :datetime="message.sent_at">{{ dateLabel(message.sent_at) }}</time>
                    </v-list-item-title>
                    <v-list-item-subtitle class="font-weight-medium text--primary">{{ message.subject }}</v-list-item-subtitle>
                    <v-list-item-subtitle>{{ message.snippet }}</v-list-item-subtitle>
                    <div class="mt-2 d-flex align-center flex-wrap inbox-chips">
                        <v-chip v-if="conversationCount(message) > 1" x-small color="blue-grey lighten-4" class="black--text">
                            <v-icon x-small left>mdi-forum-outline</v-icon>{{ conversationCount(message) }} messaggi
                        </v-chip>
                        <v-chip v-if="usefulAttachmentCount(message)" x-small outlined>
                            <v-icon x-small left>mdi-paperclip</v-icon>{{ usefulAttachmentCount(message) }}
                        </v-chip>
                        <v-chip v-if="message.mail_draft?.sla_overdue" x-small color="red lighten-4" class="black--text">
                            <v-icon x-small left>mdi-clock-alert-outline</v-icon>SLA
                        </v-chip>
                        <v-chip v-if="message.mail_draft?.assigned_to" x-small outlined>
                            <v-icon x-small left>mdi-account-outline</v-icon>{{ message.mail_draft.assigned_to.name }}
                        </v-chip>
                        <v-chip v-if="message.record?.nc_number" x-small color="green lighten-4" class="black--text">NC {{ message.record.nc_number }}</v-chip>
                        <v-chip v-if="message.workflow_status" x-small :color="`${message.workflow_status.color} lighten-4`" class="black--text">
                            {{ message.workflow_status.label }}
                        </v-chip>
                    </div>
                </v-list-item-content>
            </v-list-item>
        </v-list>

        <v-card-text v-else-if="!loading" class="empty-inbox text-center">
            <v-icon size="42" color="grey lighten-1">mdi-email-outline</v-icon>
            <div class="subtitle-2 mt-3">Nessuna conversazione da mostrare</div>
            <div class="caption grey--text mb-3">Prova a cambiare i filtri o la ricerca.</div>
            <v-btn v-if="filtersActive" small outlined color="primary" @click="clearFilters">Azzera filtri</v-btn>
        </v-card-text>

        <v-card-actions class="pagination-actions">
            <v-btn icon small :disabled="loading || pagination.page <= 1" aria-label="Pagina precedente" @click="changePage(-1)">
                <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="caption">Pagina {{ pagination.page || 1 }}</span>
            <v-btn icon small :disabled="loading || !pagination.has_more" aria-label="Pagina successiva" @click="changePage(1)">
                <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
export default {
    name: "MailSegnalazioniInbox",
    props: {
        messages: { type: Array, default: () => [] },
        selectedUid: { type: String, default: "" },
        loading: { type: Boolean, default: false },
        pagination: { type: Object, default: () => ({ page: 1, total: 0, has_more: false }) },
        filters: { type: Object, default: () => ({}) },
        operators: { type: Array, default: () => [] },
    },
    data() {
        return {
            search: this.filters.search || "",
            workFilter: this.filters.queue || "work",
            statusFilter: this.filters.status || "all",
            assignedTo: this.filters.assigned_to || "",
            overdue: this.filters.overdue === true,
            syncingFilters: false,
            searchTimer: null,
            statusOptions: [
                { text: "Tutti gli stati", value: "all" },
                { text: "Cliente da associare", value: "customer_required" },
                { text: "Articolo da verificare", value: "article_required" },
                { text: "Pronta per NC", value: "ready_for_nc" },
                { text: "NC creata", value: "nc_created" },
                { text: "8D inviato", value: "eight_d_sent" },
                { text: "Scartate", value: "handled" },
            ],
        };
    },
    computed: {
        groupedMessages() {
            const newestByConversation = new Map();
            [...this.messages]
                .sort((left, right) => new Date(right.sent_at || 0) - new Date(left.sent_at || 0))
                .forEach((message) => {
                    const key = message.conversation?.key || `uid:${message.uid}`;
                    if (!newestByConversation.has(key)) newestByConversation.set(key, message);
                });
            return [...newestByConversation.values()];
        },
        filtersActive() {
            return Boolean(String(this.search || "").trim()) || this.statusFilter !== "all" || this.workFilter !== "work" || Boolean(this.assignedTo) || this.overdue;
        },
        operatorOptions() {
            return [
                { text: "Tutti", value: "" },
                { text: "Non assegnate", value: "unassigned" },
                ...this.operators.map((item) => ({ text: item.name, value: String(item.id) })),
            ];
        },
    },
    watch: {
        filters: {
            deep: true,
            handler(value) {
                this.syncingFilters = true;
                this.search = value.search || "";
                this.workFilter = value.queue || "work";
                this.statusFilter = value.status || "all";
                this.assignedTo = value.assigned_to || "";
                this.overdue = value.overdue === true;
                this.$nextTick(() => { this.syncingFilters = false; });
            },
        },
        search() {
            if (this.syncingFilters) return;
            window.clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(this.emitFilters, 350);
        },
        workFilter() { if (!this.syncingFilters) this.emitFilters(); },
        statusFilter() { if (!this.syncingFilters) this.emitFilters(); },
        assignedTo() { if (!this.syncingFilters) this.emitFilters(); },
        overdue() { if (!this.syncingFilters) this.emitFilters(); },
    },
    beforeDestroy() {
        window.clearTimeout(this.searchTimer);
    },
    methods: {
        emitFilters() {
            this.$emit("filters-change", {
                search: String(this.search || "").trim(),
                queue: this.workFilter,
                status: this.statusFilter,
                assigned_to: this.assignedTo || "",
                overdue: this.overdue,
            });
        },
        clearFilters() {
            this.search = "";
            this.statusFilter = "all";
            this.workFilter = "work";
            this.assignedTo = "";
            this.overdue = false;
            this.$nextTick(this.emitFilters);
        },
        changePage(offset) {
            this.$emit("page-change", Math.max(1, Number(this.pagination.page || 1) + offset));
        },
        senderLabel(message) {
            return message.sender?.name || message.sender?.email || "Mittente sconosciuto";
        },
        dateLabel(value) {
            if (!value) return "";
            return new Intl.DateTimeFormat("it-IT", { day: "2-digit", month: "2-digit" }).format(new Date(value));
        },
        conversationCount(message) {
            const key = message.conversation?.key;
            const visibleCount = key ? this.messages.filter((item) => item.conversation?.key === key).length : 1;
            return Math.max(Number(message.conversation?.message_count || 1), visibleCount);
        },
        usefulAttachmentCount(message) {
            return (message.attachments || []).filter((attachment) => !attachment.hidden_by_default).length;
        },
    },
};
</script>

<style scoped>
.mail-inbox { min-height: 620px; }
.inbox-title { min-height: 68px; border-bottom: 1px solid rgba(0, 0, 0, 0.08); }
.mail-list { max-height: calc(100vh - 390px); overflow-y: auto; }
.mail-list .v-list-item { border-left: 4px solid transparent; border-bottom: 1px solid rgba(0, 0, 0, 0.06); }
.mail-list .v-list-item.selected-message { background: rgba(21, 101, 192, 0.18); border-left-color: #42a5f5; }
.queue-filter { width: 100%; }
.queue-filter .v-btn { flex: 1 1 auto; padding: 0 6px !important; }
.inbox-chips { gap: 5px; }
.empty-inbox { padding-top: 72px; }
.pagination-actions { justify-content: center; border-top: 1px solid rgba(0, 0, 0, 0.08); }
@media (max-width: 959px) {
    .mail-inbox { min-height: auto; }
    .mail-list { max-height: 360px; }
}
</style>
